import {endProgressiveTopup} from './progressive-topup-session';
import {requestPaymentDiscount} from './payment-discount-request';
// Display-only state. Prices and order eligibility remain server-authoritative.
export const COUNTDOWN_STORAGE_KEY = 'op.purchase-countdown.v1';
export function createCountdownSession({storage,wall=()=>Date.now(),mono=()=>performance.now(),onRequest=()=>{}}={}) {
    let current=null,pending=null,generation=0,anchor=0,elapsedAtAnchor=0,lastPersistedAt=0;
    const listeners=new Set();
    const persist=()=>{lastPersistedAt=mono();try{storage&&storage.setItem(COUNTDOWN_STORAGE_KEY,JSON.stringify(current));}catch(_){}};
    const emit=()=>listeners.forEach(fn=>fn(current));
    function restore(){
        if(current)return current;
        try{
            const saved=JSON.parse(storage&&storage.getItem(COUNTDOWN_STORAGE_KEY));
            if(saved&&saved.version===1&&typeof saved.key==='string'&&['ready','unavailable','loading'].includes(saved.status)){
                if(saved.status==='ready'&&(!Number.isFinite(saved.receivedWall)||!Number.isFinite(saved.elapsed)||!validSnapshot(saved.snapshot)))return null;
                current=saved;
                // A request interrupted by a document navigation does not poll/retry.
                if(current.status==='loading')current.status='unavailable';
                anchor=mono();lastPersistedAt=anchor;elapsedAtAnchor=Math.max(0,current.elapsed||0,receiptElapsed()+Math.max(0,wall()-(current.receivedWall||wall())));
            }
        }catch(_){}
        return current;
    }
    function receiptElapsed(){return current&&Number.isFinite(current.elapsedAtReceipt)?Math.max(0,current.elapsedAtReceipt):0;}
    function validSnapshot(value){
        return value&&value.schemaVersion===1&&typeof value.enabled==='boolean'&&Number.isFinite(Date.parse(value.serverNow))&&
            (!value.enabled||(/^s_[a-f0-9]{32}$/.test(value.scheduleId)&&['100','95','90','85','75','70','65'].includes(value.mode)&&Number.isFinite(Date.parse(value.endsAt))&&Date.parse(value.endsAt)>Date.parse(value.serverNow)));
    }
    function begin(key,fetchSnapshot){
        if(typeof key!=='string'||!key)return Promise.resolve(null);
        restore();
        if(current&&current.key===key)return pending||Promise.resolve(current);
        const job=++generation;
        current={version:1,key,status:'loading'};persist();emit();onRequest();
        const requestedAt=mono();
        const promise=Promise.resolve().then(fetchSnapshot).then(result=>{
            if(job!==generation)return null;
            const snapshot=result&&result.snapshot||result;
            if(!validSnapshot(snapshot))throw new Error('Invalid countdown snapshot');
            // Subtract transit time conservatively. Never lengthen the actual offer.
            const prefetchedElapsed=result&&Number.isFinite(result.elapsedMs)?Math.max(0,result.elapsedMs):0;
            elapsedAtAnchor=Math.max(0,mono()-requestedAt,prefetchedElapsed);anchor=mono();
            current={version:1,key,status:'ready',snapshot,receivedWall:wall(),elapsed:elapsedAtAnchor,elapsedAtReceipt:elapsedAtAnchor};persist();emit();return current;
        }).catch(()=>{
            if(job!==generation)return null;
            current={version:1,key,status:'unavailable'};persist();emit();return current;
        }).finally(()=>{if(job===generation)pending=null;});
        pending=promise;return promise;
    }
    function remaining(){
        restore();
        if(!current||current.status!=='ready'||!current.snapshot.enabled)return null;
        // Monotonic time handles a clock being set backwards; wall time covers sleep.
        const elapsed=Math.max(current.elapsed,elapsedAtAnchor+Math.max(0,mono()-anchor),receiptElapsed()+Math.max(0,wall()-current.receivedWall));
        current.elapsed=elapsed;
        // A small checkpoint covers an abrupt WebView termination without writing every tick.
        if(mono()-lastPersistedAt>=15000)persist();
        return Math.max(0,Date.parse(current.snapshot.endsAt)-Date.parse(current.snapshot.serverNow)-elapsed);
    }
    function checkpoint(){remaining();if(current&&current.status==='ready')persist();}
    function end(){++generation;pending=null;current=null;try{storage&&storage.removeItem(COUNTDOWN_STORAGE_KEY);}catch(_){}emit();}
    return {begin,end,remaining,checkpoint,get:restore,subscribe(fn){listeners.add(fn);return()=>listeners.delete(fn);}};
}

let singleton,prepared=null;
export function purchaseCountdown(){
    if(!singleton){let storage;try{if(typeof window!=='undefined')storage=window.sessionStorage;}catch(_){}
        singleton=createCountdownSession({storage});}
    return singleton;
}
export function preparePurchaseCountdown({gameId,transactionId}){
    if(typeof window==='undefined'||!gameId||!transactionId)return;
    const scope=JSON.stringify([String(gameId),String(transactionId)]),existing=purchaseCountdown().get();
    if(existing){
        try{const parts=JSON.parse(existing.key);if(parts[0]===String(gameId)&&parts[2]===String(transactionId))return;}catch(_){}
    }
    if(prepared&&prepared.scope===scope)return;
    // Start in parallel with the existing authenticated transaction query.
    // Keep failures handled until the transaction supplies its verified userId.
    prepared={scope,promise:requestPaymentDiscount().then(result=>({result}),error=>({error}))};
}
export function beginPurchaseCountdown({gameId,transactionId,userId}){
    if(typeof window==='undefined'||!gameId||!transactionId||!userId)return Promise.resolve(null);
    const key=JSON.stringify([String(gameId),String(userId||''),String(transactionId)]);
    const scope=JSON.stringify([String(gameId),String(transactionId)]);
    const prefetched=prepared&&prepared.scope===scope?prepared.promise:null;
    prepared=null;
    return purchaseCountdown().begin(key,async()=>{
        const captured=prefetched?await prefetched:{result:await requestPaymentDiscount()};
        if(captured.error)throw captured.error;
        const response=captured.result;
        return {snapshot:response.value.countdown,elapsedMs:Math.max(0,performance.now()-response.startedAt,Date.now()-response.startedWall)};
    });
}
export function endPurchaseCountdown(){endProgressiveTopup();prepared=null;if(typeof window!=='undefined')purchaseCountdown().end();}
export function countdownText(milliseconds){
    const seconds=Math.max(0,Math.ceil(milliseconds/1000)),days=Math.floor(seconds/86400);
    const time=[Math.floor(seconds%86400/3600),Math.floor(seconds%3600/60),seconds%60].map(n=>String(n).padStart(2,'0')).join(' : ');
    return (days?days+' 天 ':'')+time;
}
